import { useState } from "react";
import PropTypes from "prop-types";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Avatar from "@mui/material/Avatar";
import TextField from "@mui/material/TextField";
import { 
  Search, 
  Add, 
  FilterList, 
  GetApp,
  CheckCircle,
  Schedule,
  Warning,
  Cancel,
  ArrowUpward,
  ArrowDownward,
  MoreVert
} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";

// Sample inspection data
const inspectionData = [
  {
    id: "INS-1001",
    property: "Oakridge Apartments",
    unit: "3B",
    type: "Annual",
    status: "Completed",
    date: "May 15, 2025",
    inspector: "Robert Chen",
    issues: 2,
    score: 92,
  },
  {
    id: "INS-1002",
    property: "Sunnyvale Heights",
    unit: "12A",
    type: "Move-out",
    status: "Pending",
    date: "May 22, 2025",
    inspector: "Sarah Johnson",
    issues: 0,
    score: null,
  },
  {
    id: "INS-1003",
    property: "Westview Plaza",
    unit: "5C",
    type: "Quarterly",
    status: "Completed",
    date: "May 10, 2025",
    inspector: "David Martinez",
    issues: 5,
    score: 78,
  },
  {
    id: "INS-1004",
    property: "Redwood Gardens",
    unit: "101",
    type: "Emergency",
    status: "In Progress",
    date: "May 20, 2025",
    inspector: "Amy Patel",
    issues: 1,
    score: null,
  },
  {
    id: "INS-1005",
    property: "Oakridge Apartments",
    unit: "4D",
    type: "Annual",
    status: "Completed",
    date: "May 12, 2025",
    inspector: "Robert Chen",
    issues: 0,
    score: 98,
  },
  {
    id: "INS-1006",
    property: "Harbor View Lofts",
    unit: "7B",
    type: "Move-in",
    status: "Scheduled",
    date: "May 25, 2025",
    inspector: "Sarah Johnson",
    issues: 0,
    score: null,
  },
  {
    id: "INS-1007",
    property: "Sunnyvale Heights",
    unit: "8E",
    type: "Maintenance",
    status: "Scheduled",
    date: "May 27, 2025",
    inspector: "David Martinez",
    issues: 0,
    score: null,
  },
  {
    id: "INS-1008",
    property: "Westview Plaza",
    unit: "2A",
    type: "Annual",
    status: "Expired",
    date: "May 5, 2025",
    inspector: "Robert Chen",
    issues: 0,
    score: null,
  },
  {
    id: "INS-1009",
    property: "Redwood Gardens",
    unit: "205",
    type: "Quarterly",
    status: "Completed",
    date: "May 8, 2025",
    inspector: "Amy Patel",
    issues: 3,
    score: 85,
  },
  {
    id: "INS-1010",
    property: "Harbor View Lofts",
    unit: "10C",
    type: "Annual",
    status: "Scheduled",
    date: "May 24, 2025",
    inspector: "David Martinez",
    issues: 0,
    score: null,
  },
];

// Components for tabbed interface
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`inspection-tabpanel-${index}`}
      aria-labelledby={`inspection-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

// Statistics component
function StatsCard({ title, value, icon, color }) {
  return (
    <Card sx={{ height: "100%" }}>
      <ArgonBox p={2} display="flex" justifyContent="space-between">
        <ArgonBox>
          <ArgonTypography variant="button" color="text" fontWeight="medium">
            {title}
          </ArgonTypography>
          <ArgonTypography variant="h4" fontWeight="bold" mt={1}>
            {value}
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox
          display="flex"
          justifyContent="center" 
          alignItems="center"
          sx={{
            backgroundColor: color,
            width: 48,
            height: 48,
            borderRadius: "50%",
          }}
        >
          {icon}
        </ArgonBox>
      </ArgonBox>
    </Card>
  );
}

StatsCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

// Status badge component
function StatusBadge({ status }) {
  let color = "default";
  let icon = null;
  
  switch (status) {
    case "Completed":
      color = "success";
      icon = <CheckCircle fontSize="small" />;
      break;
    case "Scheduled":
      color = "primary";
      icon = <Schedule fontSize="small" />;
      break;
    case "In Progress":
      color = "info";
      icon = <Schedule fontSize="small" />;
      break;
    case "Pending":
      color = "warning";
      icon = <Warning fontSize="small" />;
      break;
    case "Expired":
      color = "error";
      icon = <Cancel fontSize="small" />;
      break;
    default:
      color = "default";
  }
  
  return (
    <Chip 
      icon={icon}
      label={status} 
      size="small" 
      color={color} 
      variant="filled"
    />
  );
}

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
};

function Inspections() {
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [statusFilter, setStatusFilter] = useState("All");
  const [propertyFilter, setPropertyFilter] = useState("All");
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Get all unique properties for filter
  const properties = ["All", ...new Set(inspectionData.map(item => item.property))];
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle filters
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };
  
  const handlePropertyFilterChange = (event) => {
    setPropertyFilter(event.target.value);
    setPage(0);
  };
  
  // Handle menu
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Handle sorting
  const handleSort = (field) => {
    const isAsc = sortField === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortField(field);
  };
  
  // Filter and sort data
  const filteredData = inspectionData.filter((inspection) => {
    const statusMatch = statusFilter === "All" || inspection.status === statusFilter;
    const propertyMatch = propertyFilter === "All" || inspection.property === propertyFilter;
    return statusMatch && propertyMatch;
  }).sort((a, b) => {
    if (sortDirection === "asc") {
      return a[sortField] > b[sortField] ? 1 : -1;
    } else {
      return a[sortField] < b[sortField] ? 1 : -1;
    }
  });
  
  // Calculate statistics
  const totalInspections = inspectionData.length;
  const completedInspections = inspectionData.filter(i => i.status === "Completed").length;
  const scheduledInspections = inspectionData.filter(i => i.status === "Scheduled" || i.status === "Pending").length;
  const issuesFound = inspectionData.reduce((sum, item) => sum + item.issues, 0);
  const averageScore = Math.round(
    inspectionData
      .filter(i => i.score !== null)
      .reduce((sum, item) => sum + item.score, 0) / 
    inspectionData.filter(i => i.score !== null).length
  );
  
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card sx={{ padding: 3, mb: 3 }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <ArgonTypography variant="h4" fontWeight="medium">
                Inspection Reports
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text" mt={1}>
                Track all scheduled and completed property inspections.
              </ArgonTypography>
            </Grid>
            <Grid item>
              <ArgonButton 
                color="info" 
                variant="contained" 
                startIcon={<Add />}
              >
                New Inspection
              </ArgonButton>
            </Grid>
          </Grid>
        </Card>

        {/* Statistics Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6} lg={3}>
            <StatsCard 
              title="Total Inspections" 
              value={totalInspections} 
              icon={<FilterList sx={{ color: "white" }} />}
              color="#3f51b5"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <StatsCard 
              title="Completed" 
              value={completedInspections} 
              icon={<CheckCircle sx={{ color: "white" }} />}
              color="#4caf50"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <StatsCard 
              title="Scheduled" 
              value={scheduledInspections} 
              icon={<Schedule sx={{ color: "white" }} />}
              color="#ff9800"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <StatsCard 
              title="Issues Found" 
              value={issuesFound} 
              icon={<Warning sx={{ color: "white" }} />}
              color="#f44336"
            />
          </Grid>
        </Grid>

        {/* Average Score Card */}
        <Card sx={{ mb: 3, p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <ArgonTypography variant="h6" fontWeight="medium">
                Average Inspection Score
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text">
                Based on completed inspections
              </ArgonTypography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={averageScore} 
                    sx={{ 
                      height: 10, 
                      borderRadius: 5,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: averageScore > 90 ? '#4caf50' : 
                                          averageScore > 80 ? '#ff9800' : '#f44336',
                      }
                    }}
                  />
                </Box>
                <Box>
                  <ArgonTypography variant="h4" color={
                    averageScore > 90 ? 'success' : 
                    averageScore > 80 ? 'warning' : 'error'
                  }>
                    {averageScore}%
                  </ArgonTypography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={2} textAlign="right">
              <ArgonButton 
                color="secondary" 
                variant="outlined" 
                size="small"
                startIcon={<GetApp />}
              >
                Reports
              </ArgonButton>
            </Grid>
          </Grid>
        </Card>

        {/* Main Content */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="inspection tabs"
            >
              <Tab label="All Inspections" {...a11yProps(0)} />
              <Tab label="Scheduled" {...a11yProps(1)} />
              <Tab label="Completed" {...a11yProps(2)} />
              <Tab label="Issues Found" {...a11yProps(3)} />
            </Tabs>
          </Box>

          {/* All Inspections Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box px={2} mb={2}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    placeholder="Search inspections..."
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: <Search color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box display="flex" justifyContent="flex-end" gap={2}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel id="status-filter-label">Status</InputLabel>
                      <Select
                        labelId="status-filter-label"
                        value={statusFilter}
                        label="Status"
                        onChange={handleStatusFilterChange}
                      >
                        <MenuItem value="All">All Statuses</MenuItem>
                        <MenuItem value="Completed">Completed</MenuItem>
                        <MenuItem value="Scheduled">Scheduled</MenuItem>
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="In Progress">In Progress</MenuItem>
                        <MenuItem value="Expired">Expired</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel id="property-filter-label">Property</InputLabel>
                      <Select
                        labelId="property-filter-label"
                        value={propertyFilter}
                        label="Property"
                        onChange={handlePropertyFilterChange}
                      >
                        {properties.map((property) => (
                          <MenuItem key={property} value={property}>
                            {property}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <ArgonButton 
                      variant="outlined" 
                      color="secondary"
                      startIcon={<FilterList />}
                    >
                      More Filters
                    </ArgonButton>
                    <IconButton 
                      aria-label="more"
                      onClick={handleMenuClick}
                    >
                      <MoreVert />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={handleMenuClose}>Export to CSV</MenuItem>
                      <MenuItem onClick={handleMenuClose}>Print Reports</MenuItem>
                      <MenuItem onClick={handleMenuClose}>Bulk Actions</MenuItem>
                      <Divider />
                      <MenuItem onClick={handleMenuClose}>Settings</MenuItem>
                    </Menu>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table sx={{ minWidth: 650 }} aria-label="inspections table">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          cursor: 'pointer' 
                        }}
                        onClick={() => handleSort('id')}
                      >
                        ID
                        {sortField === 'id' && (
                          sortDirection === 'asc' ? 
                            <ArrowUpward fontSize="small" /> : 
                            <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          cursor: 'pointer' 
                        }}
                        onClick={() => handleSort('property')}
                      >
                        Property
                        {sortField === 'property' && (
                          sortDirection === 'asc' ? 
                            <ArrowUpward fontSize="small" /> : 
                            <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          cursor: 'pointer' 
                        }}
                        onClick={() => handleSort('status')}
                      >
                        Status
                        {sortField === 'status' && (
                          sortDirection === 'asc' ? 
                            <ArrowUpward fontSize="small" /> : 
                            <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          cursor: 'pointer' 
                        }}
                        onClick={() => handleSort('date')}
                      >
                        Date
                        {sortField === 'date' && (
                          sortDirection === 'asc' ? 
                            <ArrowUpward fontSize="small" /> : 
                            <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Inspector</TableCell>
                    <TableCell>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          cursor: 'pointer' 
                        }}
                        onClick={() => handleSort('issues')}
                      >
                        Issues
                        {sortField === 'issues' && (
                          sortDirection === 'asc' ? 
                            <ArrowUpward fontSize="small" /> : 
                            <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          cursor: 'pointer' 
                        }}
                        onClick={() => handleSort('score')}
                      >
                        Score
                        {sortField === 'score' && (
                          sortDirection === 'asc' ? 
                            <ArrowUpward fontSize="small" /> : 
                            <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((inspection) => (
                    <TableRow
                      key={inspection.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        <ArgonTypography variant="button" fontWeight="medium">
                          {inspection.id}
                        </ArgonTypography>
                      </TableCell>
                      <TableCell>{inspection.property}</TableCell>
                      <TableCell>{inspection.unit}</TableCell>
                      <TableCell>{inspection.type}</TableCell>
                      <TableCell>
                        <StatusBadge status={inspection.status} />
                      </TableCell>
                      <TableCell>{inspection.date}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar 
                            sx={{ 
                              width: 24, 
                              height: 24, 
                              fontSize: '0.75rem',
                              bgcolor: 
                                inspection.inspector === "Robert Chen" ? "#3f51b5" :
                                inspection.inspector === "Sarah Johnson" ? "#9c27b0" :
                                inspection.inspector === "David Martinez" ? "#2196f3" :
                                "#f44336",
                            }}
                          >
                            {inspection.inspector.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                          <Typography variant="body2">
                            {inspection.inspector}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {inspection.issues > 0 ? (
                          <Chip 
                            label={inspection.issues} 
                            size="small" 
                            color="error" 
                            variant={inspection.issues > 3 ? "filled" : "outlined"}
                          />
                        ) : (
                          <Chip 
                            label="None" 
                            size="small" 
                            color="success" 
                            variant="outlined" 
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {inspection.score ? (
                          <Typography 
                            variant="body2" 
                            color={
                              inspection.score > 90 ? 'success.main' : 
                              inspection.score > 80 ? 'warning.main' : 'error.main'
                            }
                            fontWeight="bold"
                          >
                            {inspection.score}%
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <ArgonButton 
                          variant="contained" 
                          color="info" 
                          size="small"
                        >
                          View
                        </ArgonButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </TabPanel>

          {/* Other tabs - would include similar content */}
          <TabPanel value={tabValue} index={1}>
            <Box p={3} textAlign="center">
              <ArgonTypography variant="h6" color="text.secondary">
                Scheduled Inspections Content
              </ArgonTypography>
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <Box p={3} textAlign="center">
              <ArgonTypography variant="h6" color="text.secondary">
                Completed Inspections Content
              </ArgonTypography>
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={3}>
            <Box p={3} textAlign="center">
              <ArgonTypography variant="h6" color="text.secondary">
                Issues Found Content
              </ArgonTypography>
            </Box>
          </TabPanel>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Inspections;